// A board kept only in this browser, no account needed (spec §14.4). The same
// editor as a synced board, saved to IndexedDB instead of the server.
import type { MeResponse } from '../../shared/api.ts';
import { isTemplateId, TEMPLATES, templateObjects } from '../../shared/templates.ts';
import { newId } from '../../shared/ids.ts';
import { get } from '../api.ts';
import { Editor } from '../editor.ts';
import { attachInput } from '../input.ts';
import { exportPng, exportSvg } from '../export/export.ts';
import {
  claimLocalBoard, deleteLocalBoard, getLocalBoard, onLocalBoardChange, requestPersistence, saveLocalBoard,
} from '../local/boards.ts';
import { h } from '../util/dom.ts';
import { fileName } from '../util/download.ts';
import { createContextBar } from './context-bar.ts';
import { bindContextMenu } from './context-menu.ts';
import { createDebugOverlay } from './debug-overlay.ts';
import { bindShortcutHelp, openShortcutHelp } from './help.ts';
import { icons } from './icons.ts';
import { menuButton } from './menu.ts';
import { createMinimap } from './minimap.ts';
import { centered, mount, setTitle } from './page.ts';
import { createToolbar, createZoomControls } from './toolbar.ts';

const SAVE_DELAY_MS = 300;

/** /local/new[?template=…]: starts a board in this browser, then opens it. */
export async function newLocalBoardPage(): Promise<void> {
  const template = new URLSearchParams(location.search).get('template');
  const id = newId();
  const t = isTemplateId(template) ? TEMPLATES.find((x) => x.id === template)! : null;
  await saveLocalBoard({
    id, title: t && t.id !== 'blank' ? t.name : 'Untitled board', updatedAt: Date.now(), count: 0,
    objects: t ? templateObjects(t.id, 'local') : [],
  });
  location.replace(`/local/${id}`);
}

export async function localBoardPage(id: string): Promise<void> {
  const board = await getLocalBoard(id);
  if (!board) {
    setTitle('Not found');
    mount(centered(
      h('h1', {}, ['Not in this browser']),
      h('p', { class: 'muted' }, ["This board isn't saved in this browser. Boards without an account only exist in the browser they were made in."]),
      h('a', { class: 'btn', href: '/' }, ['Back to the start page']),
    ));
    return;
  }
  // Only one tab edits a local board; others show it read-only and follow its saves.
  const { editing, release } = await claimLocalBoard(id);
  // A page kept in the back/forward cache must not keep the board: let go when
  // it's hidden, and start over (claiming it again) if it's ever shown again.
  window.addEventListener('pagehide', (e) => { if (e.persisted) release(); });
  window.addEventListener('pageshow', (e) => { if (e.persisted) location.reload(); });
  // Signed in? Then "keep it" means moving it into your workspace rather than signing up.
  const me = await get<MeResponse>('/api/me', { redirectOn401: false }).catch(() => null);
  let title = board.title;
  setTitle(title);
  document.body.classList.add('board-mode');

  const container = h('main', { id: 'board', 'aria-label': 'Board canvas' });
  const status = h('span', { class: 'save-status', role: 'status', title: "Boards made without an account are kept in this browser. Browsers can clear them, so sign up to keep yours safe." },
    [editing ? 'Saved in this browser only' : 'Open in another tab']);
  const banner = h('div', { class: 'banner', role: 'alert', hidden: '' });
  mount(container, titleBar(), banner);

  const editor = new Editor(container, { readOnly: !editing, local: true });
  attachInput(editor);
  document.body.append(createToolbar(editor), createZoomControls(editor), createMinimap(editor), createContextBar(editor), createDebugOverlay(editor));
  bindShortcutHelp(editor);
  bindContextMenu(editor, { exportAs, notify: (msg) => showBanner(msg) });
  editor.loadObjects(board.objects);
  requestAnimationFrame(() => editor.zoomToFit());
  if (__DEV__) Object.assign(window as object, { editor });

  if (editing) {
    let timer = 0;
    let persisted = false;
    const save = () => {
      clearTimeout(timer);
      timer = 0;
      const objects = editor.store.orderedIds().map((oid) => editor.store.get(oid)!);
      saveLocalBoard({ id, title, updatedAt: Date.now(), count: objects.length, objects })
        .then(() => {
          if (!timer) status.textContent = 'Saved in this browser only';
          if (!persisted) requestPersistence();
          persisted = true;
        })
        .catch((err: unknown) => showBanner(`Couldn't save in this browser: ${(err as Error).message}`));
    };
    const schedule = () => {
      status.textContent = 'Saving…';
      clearTimeout(timer);
      timer = window.setTimeout(save, SAVE_DELAY_MS);
    };
    editor.store.subscribe(schedule);
    titleInput().addEventListener('change', schedule);
    // Leaving or hiding the page saves straight away.
    const flush = () => { if (timer) save(); };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', () => { if (document.hidden) flush(); });
  } else {
    showBanner('This board is open in another tab, so it’s read-only here. Changes made there appear here.');
    onLocalBoardChange((e) => {
      if (e.id !== id) return;
      if (e.type === 'deleted') return location.assign('/');
      void getLocalBoard(id).then((b) => {
        if (!b) return;
        editor.store.replaceAll(b.objects);
        titleInput().value = b.title;
        setTitle(b.title);
      });
    });
  }

  function titleInput(): HTMLInputElement {
    return document.querySelector('.board-title') as HTMLInputElement;
  }

  function exportAs(format: 'png' | 'svg'): void {
    const name = fileName(title) + (editor.selection.size ? ' (selection)' : '');
    (format === 'png' ? exportPng(editor, name) : exportSvg(editor, name))
      .catch((err: unknown) => showBanner(`Export failed: ${(err as Error).message}`));
  }

  function showBanner(message: string): void {
    const dismiss = h('button', { type: 'button', class: 'btn small' }, ['Dismiss']);
    dismiss.addEventListener('click', () => { banner.hidden = true; });
    banner.replaceChildren(h('span', {}, [message]), dismiss);
    banner.hidden = false;
  }

  function titleBar(): HTMLElement {
    const back = h('a', { href: '/', class: 'tb-btn', title: 'Start page', 'aria-label': 'Start page' }, ['←']);
    const input = h('input', { class: 'board-title', type: 'text', maxlength: '200', 'aria-label': 'Board title', spellcheck: 'false' });
    input.value = title;
    input.readOnly = !editing;
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') input.blur();
      if (e.key === 'Escape') {
        input.value = title;
        input.blur();
      }
    });
    input.addEventListener('change', () => {
      title = input.value.trim().replace(/\s+/g, ' ').slice(0, 200) || 'Untitled board';
      input.value = title;
      setTitle(title);
    });
    const more = menuButton('More', icons.more, () => {
      const what = editor.selection.size ? 'selection' : 'board';
      return [
        { label: `Export ${what} as PNG`, run: () => exportAs('png') },
        { label: `Export ${what} as SVG`, run: () => exportAs('svg') },
        'separator',
        { label: 'Keyboard shortcuts', hint: '?', run: openShortcutHelp },
        { label: 'Show / hide minimap', hint: 'M', run: () => editor.command('minimap') },
        ...(editing ? ['separator' as const, {
          label: 'Delete this board',
          run: () => {
            if (!confirm(`Delete "${title}" from this browser? This can't be undone.`)) return;
            void deleteLocalBoard(id).then(() => location.assign('/'));
          },
        }] : []),
      ];
    });
    const keep = me
      ? h('a', { class: 'btn small primary', href: '/', title: 'Move this board into your workspace' }, ['Move to my workspace'])
      : h('a', { class: 'btn small primary', href: '/login?signup=1', title: 'Sign up free to keep this board safe and share it' }, ['Sign up to save & share']);
    return h('div', { class: 'panel board-bar' }, [back, input, status, more, keep]);
  }
}
